/**
 * PortalHero — the sanctioned hero banner for consumer-facing portal pages
 * (member portal, employee portal). Staff pages must NOT use this — they use
 * PageHeader.
 *
 * Replaces the per-page hardcoded gradients (from-indigo-500 to-purple-600,
 * from-teal-600 to-cyan-600, from-orange-500 to-red-500, …) with three named
 * variants so the portal keeps a warm language without inventing new colors.
 */
import React from 'react'

type PortalHeroVariant = 'calm' | 'vibrant' | 'warm'

const variantClasses: Record<PortalHeroVariant, string> = {
  /** Teal/cyan — default member-portal look. */
  calm: 'bg-gradient-to-br from-teal-600 to-cyan-600',
  /** Indigo/purple — dashboards, engagement. */
  vibrant: 'bg-gradient-to-br from-indigo-500 to-purple-600',
  /** Amber/orange — wellness, celebration moments. */
  warm: 'bg-gradient-to-br from-amber-500 to-orange-600',
}

interface PortalHeroProps {
  title: React.ReactNode
  subtitle?: React.ReactNode
  variant?: PortalHeroVariant
  /** Optional leading icon/avatar block. */
  icon?: React.ReactNode
  /** Right-aligned actions. */
  actions?: React.ReactNode
  /** Extra content below the title row (stats, progress, chips). */
  children?: React.ReactNode
  className?: string
}

export function PortalHero({
  title,
  subtitle,
  variant = 'calm',
  icon,
  actions,
  children,
  className = '',
}: PortalHeroProps) {
  return (
    <div
      className={`rounded-2xl p-6 text-white shadow-v2-2 ${variantClasses[variant]} ${className}`}
    >
      <div className="flex flex-wrap items-center gap-4">
        {icon && (
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/15">
            {icon}
          </div>
        )}
        <div className="min-w-0 flex-shrink">
          <h1 className="font-display text-2xl font-semibold tracking-[-0.02em]">{title}</h1>
          {subtitle !== undefined && <p className="mt-1 text-sm text-white/80">{subtitle}</p>}
        </div>
        {actions && (
          <>
            <div className="flex-1" />
            <div className="flex flex-wrap items-center gap-2">{actions}</div>
          </>
        )}
      </div>
      {children && <div className="mt-5">{children}</div>}
    </div>
  )
}
